<template>
  <div
    ref="pillDropdown"
    class="pill-dropdown"
    :style="[primaryButtonColors, secondaryButtonColors]"
  >
    <div class="dropdown-options">
      <button
        ref="dropdown-trigger"
        class="dropdown-trigger"
        :aria-expanded="dropdownState"
        aria-haspopup="listbox"
        :aria-labelledby="dropdownState ? 'options-label' : undefined"
        :class="selectedVals.length > 0 ? 'has-selected-vals' : ''"
        @click="toggleDropdown"
        @keydown="handleTriggerKeydown"
        @focus="emit('closeSuggestionsDropdown')"
      >
        <span class="name">{{ dynamicButtonName }}</span>
        <Icon
          name="lucide:chevron-down"
          size="14"
          aria-hidden="true"
        />
      </button>

      <label
        id="options-label"
        class="sr-only"
      >
        {{ name }} {{ $t('search.options') }}
      </label>
      <div
        v-if="dropdownState"
        class="options-list"
        role="listbox"
        aria-labelledby="options-label"
        :aria-multiselectable="true"
        :aria-activedescendant="accessibility.activeDescendantId.value"
        tabindex="0"
        @keydown="handleListboxKeydown"
        @focus="handleListboxFocus"
        @blur="handleListboxBlur"
      >
        <ul
          class="options-list-ul"
          role="presentation"
        >
          <li
            v-for="(option, index) in pillOptions"
            :id="`option-${index}`"
            :ref="`option-${index}`"
            :key="index"
            role="option"
            :aria-selected="pillOptions[index].checked"
            :aria-checked="pillOptions[index].checked"
            :class="{
              'show-checkbox': name === 'Schools' || name === 'Type',
              focused: accessibility.focusedIndex.value === index,
            }"
            @click.prevent.stop="checkOption(index)"
          >
            {{ option.name }}
          </li>
        </ul>
        <div
          v-if="selectedVals.length > 0"
          class="clear-section"
        >
          <button
            class="clear-button"
            @click.prevent.stop="clearFilters"
            @keydown.enter.prevent.stop="clearFilters"
            @keydown.space.prevent.stop="clearFilters"
          >
            {{ $t('search.clear') }}
          </button>
          <button
            class="apply-button"
            @click="applyFilters"
            @keydown.enter.prevent.stop="applyFilters"
            @keydown.space.prevent.stop="applyFilters"
            @blur="handleActionButtonBlur"
          >
            {{ $t('search.apply') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { ref, useTemplateRef, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useAccessibleDropdown } from '@/composables/useAccessibleDropdown';
import { useThemedButtons } from '@/composables/useThemedButtons';
import { onClickOutside } from '@vueuse/core';

const { primaryButtonColors, secondaryButtonColors } = useThemedButtons();

const route = useRoute();
const router = useRouter();

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  name: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['closeSuggestionsDropdown']);

const dropdownState = ref(false);
const pillDropdown = useTemplateRef('pillDropdown');
const pillOptions = ref(props.options);
const selectedVals = ref([]);
const dropdownTrigger = useTemplateRef('dropdown-trigger');

const accessibility = useAccessibleDropdown({
  onClose: closeDropdown,
  scrollBehavior: 'smooth',
  typeAheadTimeout: 500,
  wrapNavigation: true,
});

function toggleDropdown() {
  dropdownState.value = !dropdownState.value;
  if (dropdownState.value) {
    nextTick(() => {
      focusListbox();
    });
  }
}

function closeDropdown() {
  dropdownState.value = false;
  accessibility.clearFocusedIndex();
  nextTick(() => {
    if (dropdownTrigger.value) {
      dropdownTrigger.value.focus();
    }
  });
}

function focusListbox() {
  const listbox = document.querySelector('.options-list');
  if (listbox) {
    listbox.focus();
    accessibility.initializeFocus(pillOptions.value, 'option', (option) => option.checked);
  }
}

function handleTriggerKeydown(event) {
  accessibility.handleTriggerKeydown(event, dropdownState.value, toggleDropdown);
}

function handleListboxKeydown(event) {
  accessibility.handleListboxKeydown(event, pillOptions.value, 'option', {
    onToggleSelection: checkOption,
    returnFocusSelector: '.dropdown-trigger',
    optionNameGetter: (option) => option.name,
    allowMultiSelect: true,
  });
}

function handleListboxFocus() {
  accessibility.handleListboxFocus(pillOptions.value, 'option', (option) => option.checked);
}

function handleListboxBlur(event) {
  const relatedTarget = event.relatedTarget;

  if (relatedTarget && pillDropdown.value?.contains(relatedTarget)) {
    return;
  }

  setTimeout(() => {
    if (pillDropdown.value && !pillDropdown.value.contains(document.activeElement)) {
      closeDropdown();
    }
  }, 150);
}

onMounted(() => {
  pillOptions.value = props.options;
});
onUnmounted(() => {
  accessibility.cleanup();
});

onClickOutside(pillDropdown, () => {
  if (dropdownState.value) {
    closeDropdown();
  }
});

function clearFiltersFromRoute() {
  const newQuery = { ...route.query };

  if (props.name === 'Schools') {
    delete newQuery.secondary_organizations;
  } else if (props.name === 'Type') {
    delete newQuery.query_indices;
  }

  router.push({
    path: route.path,
    query: newQuery,
  });
}

function checkOption(index) {
  pillOptions.value[index]['checked'] = !pillOptions.value[index]['checked'];
  selectedVals.value = pillOptions.value.filter((option) => option.checked === true);
  if (selectedVals.value.length === 0) {
    clearFiltersFromRoute();
  }
}

function clearFilters() {
  pillOptions.value.forEach((option) => {
    option.checked = false;
  });

  selectedVals.value = [];
  selectedVals.value = pillOptions.value.filter((option) => option.checked === true);
  clearFiltersFromRoute();

  nextTick(() => {
    const listbox = document.querySelector('.options-list');
    if (listbox) {
      listbox.focus();
      accessibility.setFocusedIndex(0, pillOptions.value.length, 'option');
    }
  });
}

function applyFilters() {
  if (props.name === 'Type') {
    const selectedTypes = selectedVals.value.flatMap((option) => option.type);
    router.push({
      path: route.path,
      query: { ...route.query, query_indices: selectedTypes },
    });
  } else if (props.name === 'Schools') {
    const selectedOrgs = selectedVals.value.flatMap((option) => option.org_id);
    router.push({
      path: route.path,
      query: { ...route.query, secondary_organizations: selectedOrgs },
    });
  }
  closeDropdown();
}

const dynamicButtonName = computed(() => {
  if (selectedVals.value.length >= 2) {
    return '2+ selected';
  } else if (selectedVals.value.length === 1) {
    return selectedVals.value[0].name;
  } else {
    return props.name;
  }
});

function handleActionButtonBlur(event) {
  accessibility.handleListboxBlur(event, pillDropdown, closeDropdown, [
    '.clear-button',
    '.apply-button',
    '.options-list',
  ]);
}
</script>

<style scoped lang="scss">
.pill-dropdown {
  --space-150: 6px;
  --space-200: 8px;
  --space-300: 12px;
  --space-400: 16px;
  --color-blue-500: #1850c1;
  --color-white: #fff;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-grey-950: #2d2d2d;
}

.dropdown-trigger {
  border-radius: var(--secondary-button-border-radius);
  border: var(--secondary-button-border);
  background: var(--secondary-button-background);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--space-150) var(--space-300);
  gap: var(--space-150);
  cursor: pointer;
  color: var(--secondary-button-text);

  &:focus-visible {
    outline: var(--secondary-button-focus-outline);
    outline-offset: 1px;
    border: var(--secondary-button-hover-border);
  }

  &:hover {
    background-color: var(--secondary-button-hover);
    border: var(--secondary-button-hover-border);
  }
  &.has-selected-vals {
    border-radius: var(--secondary-button-border-radius);
    border: var(--secondary-button-border);
    background: var(--secondary-button-active);
    color: var(--secondary-button-text);
  }
}

.dropdown-options {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: var(--color-white);
  margin-top: 4px;
  position: relative;
}

.name {
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.1px;
}

.options-list {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid var(--color-grey-100);
  box-sizing: border-box;
  z-index: 1;
  position: absolute;
  background: var(--color-white);
  max-height: 240px;
  min-width: 200px;
  top: 34px;
  flex-direction: column;

  &:focus-visible {
    outline: 2px solid var(--color-blue-500);
    outline-offset: -2px;
  }

  .clear-section {
    padding: 16px;
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    border-top: 1px solid var(--color-grey-200);
    background: var(--color-white);
    position: sticky;
    bottom: 0;
    z-index: 1;
    width: 100%;

    button {
      padding: var(--space-200) var(--space-400);
      border: none;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      border-radius: 4px;

      &:focus-visible {
        outline: 2px solid var(--color-blue-500);
        outline-offset: 2px;
      }

      &:hover {
        opacity: 0.8;
      }
    }

    .clear-button {
      background: var(--color-white);
      color: var(--primary-button-background);
      border: 1px solid var(--primary-button-background);
    }

    .apply-button {
      border-radius: var(--secondary-button-border-radius);
      background: var(--primary-button-background);
      color: var(--color-white);
    }
  }

  .sr-only {
    clip: rect(1px, 1px, 1px, 1px);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
  }
}

.options-list-ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
}

li {
  display: flex;
  align-items: center;
  list-style: none;
  padding: 12px 16px;
  color: var(--color-grey-950);
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 450;
  line-height: 16px;
  letter-spacing: 0.5px;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: #f0f8ff;
  }

  &.focused {
    background-color: var(--secondary-button-hover);
    margin: -1px;
  }

  &[aria-selected='true'] {
    background-color: #def;
    font-weight: 600;

    &.focused {
      background-color: var(--secondary-button-hover);
    }
  }

  &.focused[aria-selected='true'] {
    border: 2px solid var(--secondary-button-hover);
  }
}

li.show-checkbox[role='option']::before {
  position: relative;
  top: 1px;
  left: -8px;
  margin-right: 4px;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3C/svg%3E");
}

li.show-checkbox[role='option'][aria-checked='true']::before {
  position: relative;
  top: 1px;
  left: -8px;
  margin-right: 4px;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' height='16' width='16' style='forced-color-adjust: auto;'%3E%3Crect x='2' y='2' height='13' width='13' rx='2' stroke='currentcolor' stroke-width='1' fill-opacity='0' /%3E%3Cpolyline points='4,8 7,12 12,5' fill='none' stroke='currentcolor' stroke-width='2' /%3E%3C/svg%3E");
}

@media (prefers-contrast: high) {
  .dropdown-trigger:focus,
  .options-list:focus {
    outline: 3px solid;
  }

  li.focused {
    border: 3px solid;
    margin: -2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .options-list,
  li {
    transition: none;
  }
}
</style>
